import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  X,
} from 'lucide-react';
import { PROFESSIONAL_DEVELOPMENT_PATH } from '../../constants/professionalDevelopment.js';
import ResourcePrice from './ResourcePrice.jsx';

function buildSlidesFromModules(modules) {
  return (modules || [])
    .map((mod, index) => {
      const imageSection = mod.sections?.find((s) => s.type === 'image');
      if (!imageSection?.src) return null;
      const hero = mod.sections?.find((s) => s.type === 'hero');
      return {
        id: mod.id || `slide-${index}`,
        index,
        title: mod.title || hero?.title || `Slide ${index + 1}`,
        src: imageSection.src,
        alt: imageSection.alt || mod.title,
      };
    })
    .filter(Boolean);
}

function SlideDeckViewer({ resource, slides }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const stageRef = useRef(null);
  const thumbRailRef = useRef(null);
  const activeThumbRef = useRef(null);

  const total = slides.length;
  const activeSlide = slides[activeIndex];

  const goTo = useCallback(
    (index) => {
      if (index < 0 || index >= total) return;
      setActiveIndex(index);
    },
    [total]
  );

  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  const toggleFullscreen = useCallback(async () => {
    if (!stageRef.current) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await stageRef.current.requestFullscreen();
      }
    } catch (e) {
      console.warn('Fullscreen not available', e);
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    activeThumbRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [activeIndex]);

  useEffect(() => {
    stageRef.current?.focus();
  }, []);

  const handleStageKeyDown = (e) => {
    const tag = e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) {
      return;
    }

    if (e.key === 'ArrowRight' || e.key === ' ') {
      e.preventDefault();
      goNext();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    } else if (e.key === 'Escape' && document.fullscreenElement) {
      document.exitFullscreen();
    } else if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      toggleFullscreen();
    }
  };

  const scrollThumbs = (direction) => {
    const rail = thumbRailRef.current;
    if (!rail) return;
    rail.scrollBy({ left: direction * 280, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0f1419] text-white flex flex-col">
      {!isFullscreen && (
        <header className="shrink-0 border-b border-white/10 bg-[#0f1419]/95 backdrop-blur-sm z-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
            <Link
              to={PROFESSIONAL_DEVELOPMENT_PATH}
              className="inline-flex items-center gap-2 text-sm text-emerald-300/90 hover:text-white font-medium shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Professional Development</span>
            </Link>
            <div className="min-w-0 text-center flex-1">
              <h1 className="text-base sm:text-lg font-semibold truncate">{resource.title}</h1>
              {resource.subtitle && (
                <p className="text-xs text-slate-400 truncate hidden sm:block">{resource.subtitle}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <ResourcePrice resource={resource} variant="dark" />
              <div className="text-sm text-slate-400 tabular-nums">
                {activeIndex + 1} / {total}
              </div>
            </div>
          </div>
        </header>
      )}

      <div
        ref={stageRef}
        tabIndex={0}
        role="region"
        aria-label={`${resource.title} slide deck`}
        onKeyDown={handleStageKeyDown}
        className={`flex-1 flex flex-col min-h-0 outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50 ${
          isFullscreen ? 'bg-black h-screen w-screen' : 'bg-[#0f1419]'
        }`}
      >
        {isFullscreen && (
          <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
            <p className="text-sm text-white/80 pointer-events-auto">
              {resource.title} · {activeIndex + 1}/{total}
            </p>
            <button
              type="button"
              onClick={() => document.exitFullscreen()}
              className="pointer-events-auto p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Exit fullscreen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        <div
          className={`flex-1 flex items-center justify-center relative min-h-0 ${
            isFullscreen ? 'px-4 pt-12 pb-4' : 'px-4 sm:px-8 py-6 sm:py-10'
          }`}
        >
          <button
            type="button"
            onClick={goPrev}
            disabled={activeIndex === 0}
            className="absolute left-2 sm:left-4 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-20 disabled:pointer-events-none transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
          </button>

          <button
            type="button"
            onClick={toggleFullscreen}
            className="group relative w-full max-w-5xl mx-8 sm:mx-16 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 rounded-lg overflow-hidden"
            aria-label="View slide fullscreen"
          >
            <div className="aspect-[16/10] w-full bg-[#1a2332] rounded-lg shadow-2xl shadow-black/50 ring-1 ring-white/10 overflow-hidden">
              <img
                key={activeSlide.src}
                src={activeSlide.src}
                alt={activeSlide.alt}
                className="w-full h-full object-contain bg-[#0a0e14]"
                draggable={false}
              />
            </div>
            {!isFullscreen && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors rounded-lg">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 text-sm font-medium bg-black/60 px-4 py-2 rounded-full">
                  <Maximize2 className="w-4 h-4" />
                  Click for fullscreen
                </span>
              </div>
            )}
          </button>

          <button
            type="button"
            onClick={goNext}
            disabled={activeIndex === total - 1}
            className="absolute right-2 sm:right-4 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-20 disabled:pointer-events-none transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
          </button>
        </div>

        <div
          className={`shrink-0 border-t border-white/10 bg-[#0a0e14] ${
            isFullscreen ? 'pb-safe' : ''
          }`}
        >
          <div className="max-w-6xl mx-auto px-2 sm:px-4 py-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollThumbs(-1)}
                className="shrink-0 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors hidden sm:flex"
                aria-label="Scroll thumbnails left"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div
                ref={thumbRailRef}
                className="flex-1 flex gap-2 sm:gap-3 overflow-x-auto scroll-smooth py-1 px-1 scrollbar-thin scrollbar-thumb-white/20"
                style={{ scrollbarWidth: 'thin' }}
              >
                {slides.map((slide, index) => {
                  const isActive = index === activeIndex;
                  return (
                    <button
                      key={slide.id}
                      ref={isActive ? activeThumbRef : null}
                      type="button"
                      onClick={() => goTo(index)}
                      className={`shrink-0 w-[88px] sm:w-[112px] rounded-md overflow-hidden transition-all ${
                        isActive
                          ? 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-[#0a0e14] scale-105'
                          : 'opacity-60 hover:opacity-100 ring-1 ring-white/10'
                      }`}
                      aria-label={`Go to slide ${index + 1}: ${slide.title}`}
                      aria-current={isActive ? 'true' : undefined}
                    >
                      <div className="aspect-[16/10] bg-[#1a2332]">
                        <img
                          src={slide.src}
                          alt=""
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => scrollThumbs(1)}
                className="shrink-0 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors hidden sm:flex"
                aria-label="Scroll thumbnails right"
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              <button
                type="button"
                onClick={toggleFullscreen}
                className="shrink-0 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors ml-1"
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? (
                  <Minimize2 className="w-5 h-5" />
                ) : (
                  <Maximize2 className="w-5 h-5" />
                )}
              </button>
            </div>

            {!isFullscreen && (
              <p className="text-center text-xs text-slate-500 mt-3 hidden sm:block">
                Focus the slide area, then use arrow keys · Click slide for fullscreen ·{' '}
                <kbd className="px-1.5 py-0.5 rounded bg-white/10">F</kbd> toggle fullscreen
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { buildSlidesFromModules };
export default SlideDeckViewer;
